import * as React from 'react';

import { ArrowLeftRight, ChevronDown, Copy, Plus } from 'lucide-react';
import { useRouter } from 'next/router';

import { Box, IconButton, List, Menu, Tooltip } from '@mui/material';

import { VERIFICATION_STATUS } from '@itp/prisma-talent/generated/client';
import { useUser } from '@itp/talent';
import { Avatar } from '@itp/talent/components/core/avatar';
import { BodyText, BodyTextBold } from '@itp/talent/components/core/typography';
import { getRoutingPath, ROUTES } from '@itp/talent/constants/routingPath';
import { handleRouteChange } from '@itp/talent/utils/handleRouteChange';

import { DDMenuItem } from '../../navigation/topNavigation/topNavigation.styles';

interface CompanyWidgetProps {
  companyLogo?: null | string;
  companyName: string;
  onShareClick: () => void;
}

export const CompanyWidget: React.FC<CompanyWidgetProps> = ({
  companyLogo,
  companyName,
  onShareClick,
}) => {
  const router = useRouter();
  const { setUser, user } = useUser();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const verifiedCompanies = user?.companies?.filter(
    (c) => c?.company.verificationStatus === VERIFICATION_STATUS.verified,
  );

  const hasMultipleCompanies = verifiedCompanies && verifiedCompanies.length > 1;

  const handleCompanySwitch = (companyId: string) => {
    setUser((prev) => ({ ...prev, selectedCompanyId: companyId }));
    localStorage.setItem('selectedCompanyId', companyId);
    setAnchorEl(null);
    handleRouteChange({ toHref: getRoutingPath(ROUTES.COMPANY_DASHBOARD) }, router?.push);
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        mb: 1,
        px: 1,
        py: 1,
      }}
    >
      <Avatar
        alt={companyName}
        size="small"
        src={companyLogo || undefined}
        sx={{ flexShrink: 0, height: 28, width: 28 }}
      >
        {companyName?.charAt(0)}
      </Avatar>

      <BodyTextBold
        sx={{
          flex: 1,
          fontSize: '0.875rem',
          lineHeight: 1.3,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        suppressHydrationWarning
      >
        {companyName}
      </BodyTextBold>

      {hasMultipleCompanies && (
        <Tooltip title="Switch company">
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            sx={{ flexShrink: 0, p: 0.5 }}
          >
            <ChevronDown size={16} />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Copy link">
        <IconButton onClick={onShareClick} size="small" sx={{ flexShrink: 0, p: 0.5 }}>
          <Copy size={14} />
        </IconButton>
      </Tooltip>

      {hasMultipleCompanies && (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          onClose={() => setAnchorEl(null)}
          open={open}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        >
          <List dense disablePadding>
            {verifiedCompanies.map(({ company }) => {
              if (company.id === user.selectedCompanyId) return null;
              return (
                <DDMenuItem
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    py: 0.75,
                  }}
                  key={company.id}
                  onClick={() => handleCompanySwitch(company.id)}
                >
                  <ArrowLeftRight size={16} style={{ flexShrink: 0 }} />
                  <BodyText
                    sx={{
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {company.name}
                  </BodyText>
                </DDMenuItem>
              );
            })}
            <DDMenuItem
              sx={{
                alignItems: 'center',
                display: 'flex',
                py: 0.75,
              }}
              component="a"
              href={getRoutingPath(ROUTES.COMPANY_CREATE)}
            >
              <Plus size={16} style={{ flexShrink: 0 }} />
              <BodyText>Create company</BodyText>
            </DDMenuItem>
          </List>
        </Menu>
      )}
    </Box>
  );
};
